import { useQueries } from "@tanstack/react-query";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { fetchCategoryData } from "../../../../common/queries/shared/industryAnalysisQueryUtils";
import type { RawData, RawDataPop } from "../types";

const ENDPOINT = "api/Market/MarketResearch/Overview";
const POP_ENDPOINT = "api/Market/TrafficAndEngagement/OverviewCompare";

export interface IKeyIndicatorsData {
    rawData?: RawData;
    rawDataPop?: RawDataPop;
}

export const useKeyIndicatorsData = (categories: Array<ICategory | ICustomCategory>) => {
    const isPop = DurationService.isPeriodOverPeriod();
    const { duration, comparedDuration, country, includeSubdomains } = proNavigator.getParams();

    return useQueries({
        queries: categories.map((category) => ({
            queryKey: [
                "keyIndicators",
                category.id,
                category.categoryHash,
                duration,
                comparedDuration,
                country,
                includeSubdomains,
            ],
            queryFn: async (): Promise<IKeyIndicatorsData> => {
                if (isPop) {
                    const rawDataPop = await fetchCategoryData<RawDataPop>(POP_ENDPOINT, category);
                    return { rawDataPop };
                }

                const rawData = await fetchCategoryData<RawData>(ENDPOINT, category);
                return { rawData };
            },
        })),
        combine: (results) => {
            return {
                data: results.map((result) => result.data).filter(Boolean),
                isLoading: results.some((result) => result.isLoading),
                isError: results.some((result) => result.isError),
                error: results.find((result) => result.error)?.error || null,
            };
        },
    });
};
